import { z } from 'zod';
import { mediaIdSchema, mediaTypeSchema, type MediaType } from './content';

export interface MediaSnapshot {
  title: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  episodeTitle?: string | null;
  year?: number | null;
}

export interface HistoryEntry {
  id: string;
  mediaId: string;
  contentType: MediaType;
  seasonNumber: number | null;
  episodeNumber: number | null;
  position: number;
  duration: number;
  completed: boolean;
  lastWatchedAt: string;
  hiddenFromContinue: boolean;
  snapshot: MediaSnapshot | null;
  /** Continue Watching only: the series' next episode, not started yet (position 0). */
  upNext?: boolean;
}

export interface WatchlistEntry {
  id: string;
  mediaId: string;
  contentType: MediaType;
  addedAt: string;
  snapshot: MediaSnapshot | null;
}

export interface LibraryItem {
  mediaId: string;
  contentType: MediaType;
  snapshot: MediaSnapshot | null;
  saved: boolean;
  savedAt: string | null;
  watched: boolean;
  inProgress: boolean;
  progress: number | null;
  remaining: number | null;
  lastWatchedAt: string | null;
  episodesWatched: number;
  latestEpisode: { season: number; episode: number; title: string | null } | null;
  /** A series whose next episode is waiting (it counts as in progress). */
  nextEpisode?: { season: number; episode: number; title: string | null } | null;
}

export const addWatchlistSchema = z.object({
  mediaId: mediaIdSchema,
  contentType: mediaTypeSchema,
  snapshot: z
    .object({
      title: z.string().max(300),
      posterUrl: z.string().max(2000).nullable(),
      backdropUrl: z.string().max(2000).nullable(),
      year: z.number().int().nullable().optional(),
    })
    .optional(),
});

export const markWatchedSchema = z.object({
  mediaId: mediaIdSchema,
  contentType: mediaTypeSchema,
  seasonNumber: z.number().int().min(0).nullable().optional(),
  episodeNumber: z.number().int().min(0).nullable().optional(),
  watched: z.boolean(),
  snapshot: addWatchlistSchema.shape.snapshot,
});

/** Several episodes of one season at once (Mark season as watched, everything up to here). */
export const markEpisodesSchema = z.object({
  mediaId: mediaIdSchema,
  seasonNumber: z.number().int().min(0),
  episodes: z.array(z.number().int().min(0)).min(1).max(300),
  watched: z.boolean(),
  snapshot: addWatchlistSchema.shape.snapshot,
});

export interface ProgressLookup {
  latest: HistoryEntry | null;
  episodes: Record<string, { position: number; duration: number; completed: boolean }>;
}
